import {Dispatch, MouseEvent, SetStateAction, useEffect, useState} from 'react';
import {shallowEqual, useSelector} from 'react-redux';
import {useHistory} from 'react-router-dom';
import AccountCircleOutlinedIcon from '@mui/icons-material/AccountCircleOutlined';
import ArrowDropDownOutlinedIcon from '@mui/icons-material/ArrowDropDownOutlined';
import CallEndOutlinedIcon from '@mui/icons-material/CallEndOutlined';
import PhoneIcon from '@mui/icons-material/Phone';
import PhoneForwardedOutlinedIcon from '@mui/icons-material/PhoneForwardedOutlined';
import {Grid, Popover} from '@mui/material';

import {MHButton, MHToolTip, MHTypography} from '@/components/base';
import {AvailableCallTransferAgents} from '@/components/general/AvailableCallTransferAgents';
import {AGENT_STATUSES} from '@/constants/constants';
import routes from '@/constants/routes';
import awsConnectService from '@/services/awsConnectApiService';

import {phoneCallDataSelector} from '../../redux/selectors';
import {getUserName} from '../shared/utils';

import commonStyles from '../shared/common.module.scss';
import styles from './callControls.module.scss';

export const CallControls = () => {
    const history = useHistory();
    const [activeAgentsIsOpen, setActiveAgentsIsOpen] = useState(false);
    const [activeAgentsAnchorElement, setActiveAgentsAnchorElement] = useState(null);

    const {
        isOutboundCallActive,
        isIncomingCall,
        callerPatientId,
        callerFirstName,
        callerLastName,
        callerSex,
        callerDOB,
        awsConnectAgentStatus,
    } = useSelector(phoneCallDataSelector, shallowEqual);

    const toggleTransfersPopover = (event: Dispatch<SetStateAction<boolean>> | MouseEvent) => {
        // activeAgentsIsOpen can be either true or false
        const nextAnchorState = !activeAgentsIsOpen && 'currentTarget' in event ? event.currentTarget : null;
        setActiveAgentsIsOpen(!activeAgentsIsOpen);
        setActiveAgentsAnchorElement(nextAnchorState);
    };

    const formattedPatientInfo = () => {
        const patientName = getUserName({callerFirstName, callerLastName});
        const displayName = patientName.length > 20 ? `${patientName.slice(0, 17)}...` : patientName;

        return (
            <>
                <MHToolTip tooltipContent={patientName} light>
                    <MHTypography variant="bodyM" color="white">
                        {displayName}
                    </MHTypography>
                </MHToolTip>
                {(callerDOB || callerSex) && (
                    <MHTypography variant="bodyM" color="white">
                        ({callerDOB}
                        {callerDOB && callerSex && ', '}
                        {callerSex?.[0]?.toUpperCase()})
                    </MHTypography>
                )}
            </>
        );
    };

    useEffect(() => {
        if (!isOutboundCallActive) setActiveAgentsIsOpen(false);
    }, [isOutboundCallActive, setActiveAgentsIsOpen]);

    const {disconnectContact} = awsConnectService;
    const isTransferCallAvailable = awsConnectAgentStatus === AGENT_STATUSES.BUSY;

    return (
        <>
            <div className={styles.incommingCallWrapper}>
                <PhoneIcon />
                <Grid display="grid" className={styles.patientInfoText}>
                    <MHTypography variant="bodyM" color="white">
                        {isIncomingCall ? 'Incoming Call' : 'Outgoing Call'}
                    </MHTypography>
                    {isIncomingCall && formattedPatientInfo()}
                </Grid>
            </div>
            <Grid className={`${commonStyles.gridBorder} ${isOutboundCallActive && commonStyles.active}`} item>
                {callerPatientId && (
                    <MHButton
                        buttonType="primaryThird"
                        className={styles.transferButton}
                        onClickHandler={() => history.push(routes.PATIENT_SINGLE_OVERVIEW(callerPatientId.toString()))}
                    >
                        <AccountCircleOutlinedIcon />
                        <span>Go to Patient</span>
                    </MHButton>
                )}
                <MHButton
                    disabled={!isTransferCallAvailable}
                    buttonType="primary"
                    onClickHandler={toggleTransfersPopover}
                    className={styles.transferButton}
                >
                    <PhoneForwardedOutlinedIcon />
                    <span>Transfer</span>
                    <ArrowDropDownOutlinedIcon />
                </MHButton>
                <MHButton buttonType="primary" onClickHandler={disconnectContact} className={styles.endCallButton}>
                    <CallEndOutlinedIcon />
                    <span>End Call</span>
                </MHButton>
            </Grid>
            <Popover
                open={activeAgentsIsOpen}
                anchorEl={activeAgentsAnchorElement}
                classes={{root: styles.popover}}
                onClose={toggleTransfersPopover}
                anchorOrigin={{
                    vertical: 80,
                    horizontal: 0,
                }}
            >
                <AvailableCallTransferAgents {...{handlePopoverClose: toggleTransfersPopover}} />
            </Popover>
        </>
    );
};
